import { useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { HERO_ART } from '@/assets/heroes';
import { PageHero } from '@/components/layout/PageHero';
import { HeroArt } from '@/components/media/HeroArt';
import { Section } from '@/components/ui/Section';
import { SITE_NAME, absoluteUrl } from '@/lib/config';
import { useT } from '@/lib/i18n';
import { Seo } from '@/lib/seo';
import { usePublicSettings } from '@/lib/site/public-settings';
import { Reveal } from '@/motion/Reveal';
import { ContactDetails } from './ContactDetails';
import { content } from './content';
import { EnquiryForm } from './EnquiryForm';
import { parseEnquiryPrefill } from './prefill';
import './contact.css';

/**
 * `/contact-us` — how to reach the firm and the enquiry form. `/contact-us#enquiry` (the Enquiry button on
 * every card and product page) scrolls to the form and moves focus to its heading; the product,
 * variant, topic and type it was opened with pre-fill the form.
 */
export function ContactPage() {
  const copy = content;
  const t = useT();
  const settings = usePublicSettings();
  const { hash } = useLocation();
  const [params] = useSearchParams();
  const search = params.toString();
  // the prefill is read once per visit to the page: editing the form must not be overwritten by it
  const prefill = useMemo(() => parseEnquiryPrefill(new URLSearchParams(search)), [search]);

  useEffect(() => {
    if (hash !== '#enquiry') return undefined;
    // wait for the layout to settle (fonts, the first paint), then bring the form into view
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById('enquiry');
      target?.scrollIntoView({ block: 'start' });
      document.getElementById('enquiry-heading')?.focus({ preventScroll: true });
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [hash]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: copy.seoTitle,
    url: absoluteUrl('/contact-us'),
    about: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: absoluteUrl('/'),
      ...(settings.phone ? { telephone: settings.phone } : {}),
      ...(settings.email ? { email: settings.email } : {}),
    },
  };

  return (
    <>
      <Seo
        title={copy.seoTitle}
        description={copy.seoDescription}
        path="/contact-us"
        jsonLd={[jsonLd]}
      />
      <PageHero
        breadcrumbs={[{ label: t('nav.home'), to: '/' }, { label: t('nav.contact') }]}
        eyebrow={copy.eyebrow}
        title={copy.title}
        lead={copy.lead}
        compact
        className="film-hero film-hero--medium on-dark"
        media={<HeroArt art={HERO_ART.contact} />}
      />
      <Section className="contact" tone="bg" divider={false}>
        <div className="contact__grid">
          <Reveal className="contact__details">
            <ContactDetails />
          </Reveal>
          <section id="enquiry" className="contact__form" aria-labelledby="enquiry-heading">
            <div className="contact__card">
              <EnquiryForm key={search} prefill={prefill} />
            </div>
          </section>
        </div>
      </Section>
    </>
  );
}
